import { useMemo, useState } from 'preact/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { route } from 'preact-router';

import { T, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { CoinGlyph } from '@/ui/primitives/CoinGlyph';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { catalogRepo } from '@/data/repositories/catalog-repo';
import { homeRepo } from '@/data/repositories/home-repo';
import { fmt, compact, fillWindow, queueTime } from '@/ui/utils/format';
import { config } from '@/config';
import type { Service } from '@/domain/types';
import type { ComponentChildren, JSX } from 'preact';

function titleCase(s: string) {
  return s.replace(/\b\w/g, c => c.toUpperCase());
}

export function BuyScreen({ serviceId }: { serviceId: string }) {
  const qc = useQueryClient();
  const id = Number(serviceId);

  const platforms = useQuery({
    queryKey: ['platforms'],
    queryFn: catalogRepo.platforms,
    staleTime: config.catalog.staleTimeMs,
  });

  const lookup = useQuery({
    queryKey: ['services-flat'],
    queryFn: async () => {
      const ps = await catalogRepo.platforms();
      const lists = await Promise.all(ps.map(p => catalogRepo.services(p.id!).then(ss => ({ p, ss }))));
      const map = new Map<number, Service>();
      for (const { p, ss } of lists) for (const s of ss) map.set(s.id!, { ...s, platform: p.name });
      return map;
    },
    staleTime: config.catalog.staleTimeMs,
  });

  const home = useQuery({ queryKey: ['home'], queryFn: homeRepo.home });

  const s = lookup.data?.get(id);
  const isSpecial = s?.platform?.toLowerCase() === 'special';

  const [amount, setAmount] = useState<number>(0);
  const [link, setLink] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');

  useMemo(() => {
    if (s && amount === 0) setAmount(s.min ?? 0);
  }, [s]);

  const buy = useMutation({
    mutationFn: () => catalogRepo.buy({
      serviceId: id, amount, link,
      notifEmail: email || undefined, description: description || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      route(config.routes.orders);
    },
  });

  const total = s ? Math.ceil(amount * (s.costper ?? 0)) : 0;
  const validAmount = !!s && amount >= (s.min ?? 0) && amount <= (s.max ?? 0);
  const hasRequiredDetails = isSpecial
    ? !!link.trim() && !!email.trim() && !!description.trim()
    : !!link.trim();
  const canBuy = !!s && validAmount && hasRequiredDetails && (home.data?.coins ?? 0) >= total && !buy.isPending;

  if (lookup.isPending || platforms.isPending) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner/></div>;
  }
  if (!s) {
    return <ErrorBox message="Service not found." onRetry={() => lookup.refetch()}/>;
  }

  return (
    <div style={{ background: T.bg, minHeight: '100%', position: 'relative' }}>
      <TopBar title="New Order" onBack={() => history.back()}
        right={
          <div style={{
            padding: '6px 12px', borderRadius: 10, background: T.surface,
            border: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <CoinGlyph size={14}/>
            <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.text,
              fontVariantNumeric: 'tabular-nums' }}>{fmt(home.data?.coins ?? 0)}</span>
          </div>
        }
      />

      <div style={{ padding: '0 20px 220px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...card, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 14,
              background: 'rgba(198,255,61,0.10)', border: `1px solid rgba(198,255,61,0.20)`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: T.accent, flexShrink: 0,
            }}>{sized(Icons.heart, 22)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>{titleCase(s.name ?? '')}</div>
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap',
                gap: '2px 10px', fontSize: 11, color: T.textDim,
                fontFamily: T.fontMono, fontVariantNumeric: 'tabular-nums' }}>
                <span>ID {s.id}</span>
                <span style={{ color: T.line2 }}>•</span>
                <span>Min {fmt(s.min ?? 0)}</span>
                <span style={{ color: T.line2 }}>•</span>
                <span>Max {fmt(s.max ?? 0)}</span>
                {!!s.filltime && (<>
                  <span style={{ color: T.line2 }}>•</span>
                  <span>{fillWindow(s.filltime)} fill</span>
                </>)}
                <span style={{ color: T.line2 }}>•</span>
                <span style={{ color: T.accent }}>{s.costper} C / unit</span>
              </div>
              {s.avgqueuetime != null && (
                <div style={{ marginTop: 6, fontSize: 12, color: T.textDim }}>
                  Average queue time: <span style={{ color: T.text, fontFamily: T.fontMono }}>{queueTime(s.avgqueuetime)}</span>
                </div>
              )}
            </div>
          </div>
          {s.description && (
            <div style={{ marginTop: 14, fontSize: 13, color: T.textDim, lineHeight: 1.7 }}>
              {s.description.split('\n').filter(Boolean).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label={isSpecial ? 'Project request' : 'Amount'} hint={isSpecial ? '1 request' : `${fmt(s.min ?? 0)} – ${fmt(s.max ?? 0)}`}>
            {isSpecial ? (
              <div style={{
                ...inputStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>Custom service review</span>
                <span style={{ color: T.textDim, fontFamily: T.fontMono }}>x1</span>
              </div>
            ) : (
              <>
                <input
                  type="number"
                  value={amount}
                  onInput={(e) => setAmount(Number((e.target as HTMLInputElement).value) || 0)}
                  placeholder="Enter amount"
                  style={inputStyle}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {quickAmounts(s.min ?? 0, s.max ?? 0).map(v => (
                    <button key={v} onClick={() => setAmount(v)} style={{
                      padding: '6px 10px', borderRadius: 8,
                      background: amount === v ? T.accent : T.surface2,
                      color: amount === v ? T.accentInk : T.textDim,
                      border: `1px solid ${amount === v ? T.accent : T.line}`,
                      fontSize: 11, fontFamily: T.fontMono, cursor: 'pointer',
                    }}>{compact(v)}</button>
                  ))}
                </div>
              </>
            )}
          </Field>

          <Field label={isSpecial ? 'Project / reference link' : 'Target link'} leadIcon={Icons.link}>
            <input
              value={link}
              onInput={(e) => setLink((e.target as HTMLInputElement).value)}
              placeholder={isSpecial ? 'Website, app, reference, or brief link' : 'https://'}
              style={inputStyle}
            />
          </Field>

          <Field label={isSpecial ? 'Contact email' : 'Notification email'} leadIcon={Icons.mail} optional={!isSpecial}>
            <input
              type="email"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="example@example.com"
              style={inputStyle}
            />
          </Field>

          <Field label="Description" optional={!isSpecial}>
            <textarea
              value={description}
              onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              placeholder={isSpecial ? 'Describe your project, goals, required features, deadline, and anything we should know.' : 'Add a note for this order…'}
              rows={isSpecial ? 5 : 3}
              style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }}
            />
          </Field>
        </div>

        {buy.isError && (
          <div style={{ fontSize: 13, color: T.danger, padding: '8px 4px' }}>
            {(buy.error as Error).message}
          </div>
        )}
      </div>

      <div style={{
        position: 'fixed', bottom: 'calc(72px + var(--safe-bottom, 0px))', left: 0, right: 0,
        padding: '12px 20px',
        background: `linear-gradient(to top, ${T.bg} 60%, rgba(10,12,15,0))`,
        zIndex: 35,
      }}>
        <div style={{
          background: T.surface, border: `1px solid ${T.line2}`, borderRadius: 18,
          padding: 14, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1, fontWeight: 500 }}>Total cost</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
              <span style={{ fontFamily: T.fontMono, fontSize: 22, fontWeight: 500,
                color: T.text, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
                {fmt(total)}
              </span>
              <span style={{ color: T.accent, fontSize: 12, fontWeight: 600 }}>C</span>
            </div>
          </div>
          <button onClick={() => buy.mutate()} disabled={!canBuy} style={{
            padding: '12px 18px', borderRadius: 12,
            background: canBuy ? T.accent : T.surface2,
            color: canBuy ? T.accentInk : T.textMute,
            border: 'none', fontSize: 13, fontWeight: 700,
            cursor: canBuy ? 'pointer' : 'not-allowed',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            {buy.isPending && <Spinner size={14}/>}
            Buy Now {sized(Icons.arr, 16, 2)}
          </button>
        </div>
      </div>
    </div>
  );
}

function quickAmounts(min: number, max: number): number[] {
  const out: number[] = [];
  const candidates = [100, 500, 1000, 5000, 10000, 50000, 100000];
  for (const v of candidates) if (v >= min && v <= max) out.push(v);
  return out.slice(0, 4);
}

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 12,
  background: T.surface, border: `1px solid ${T.line}`,
  color: T.text, fontFamily: T.font, fontSize: 14, outline: 'none',
  boxSizing: 'border-box' as const,
};

function Field({
  label, hint, leadIcon, optional, children,
}: {
  label: string; hint?: string; leadIcon?: JSX.Element; optional?: boolean;
  children: ComponentChildren;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 6, padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
            letterSpacing: 1, fontWeight: 500 }}>{label}</span>
          {optional && <span style={{ fontSize: 10, color: T.textMute }}>· Optional</span>}
        </div>
        {hint && <span style={{ fontSize: 11, color: T.textMute, fontFamily: T.fontMono }}>{hint}</span>}
      </div>
      <div style={{ position: 'relative' }}>
        {leadIcon && (
          <span style={{ position: 'absolute', left: 12, top: 14,
            color: T.textMute, pointerEvents: 'none' }}>
            {sized(leadIcon, 16)}
          </span>
        )}
        <div style={leadIcon ? { paddingLeft: 36 } : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}
